import axios from "axios";
import React, { useEffect, useState, useRef } from "react";
import StickyTable from "../../components/common/StickyTable";
import {
  WEEKLY_FEES_EDIT,
  WEEKLY_FEES_UPDATE,
  MEETING_WEEKLY_FEES_LIST,
  USER_LIST_DROPDOWN,
  COUNTRY_LIST_DROPDOWN,
  STATE_LIST_DROPDOWN,
  CHAPTER_LIST_DROPDOWN,
  REGION_LIST_DROPDOWN,
} from "../../routes/routes";
import { PAGINATE } from "../../constant/constant";
import { toast } from "react-toastify";
import { NoDataFound, Pagination, hasRole } from "../../components/Utils";
import { usePermission } from "../../hooks/usePermission";
import Loader from "../../components/common/Loader"; // Import the loader component
import Select from "react-select";
import { Modal } from "react-bootstrap";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Link } from "react-router-dom";

const WeeklyFeesList = () => {
  // ## State Variable Declaration
  const { hasPermission } = usePermission();
  const [data, setData] = useState([]);
  const [paginator, setPaginator] = useState({});
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [countryOptions, setCountryOptions] = useState({});
  const [stateOptions, setStateOptions] = useState({});
  const [regionOptions, setRegionOptions] = useState({});
  const [chapterOptions, setChapterOptions] = useState({});
  const [filter, setFilter] = useState([]);
  const applyFilter = (next) => {
    setPage(1);
    setFilter(next);
  };

  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(false); // New state to trigger refresh
  const [userOptions, setUserOptions] = useState({});
  const [showClearBtn, setShowClearBtn] = useState(false);
  const [feesModal, setFeesModal] = useState(false);
  const [feesData, setFeesData] = useState({});
  const [errors, setErrors] = useState({});

  const datePickerRef = useRef(null);
  const selectStateRef = useRef(null);
  const selectRegionRef = useRef(null);
  const selectChapterRef = useRef(null);
  const selectMemberRef = useRef(null);

  const handleStateClear = () => {
    if (selectStateRef.current) selectStateRef.current.clearValue();
    if (selectRegionRef.current) selectRegionRef.current.clearValue();
    if (selectChapterRef.current) selectChapterRef.current.clearValue();
    if (selectMemberRef.current) selectMemberRef.current.clearValue();
  };

  const handleRegionClear = () => {
    if (selectRegionRef.current) selectRegionRef.current.clearValue();
    if (selectChapterRef.current) selectChapterRef.current.clearValue();
    if (selectMemberRef.current) selectMemberRef.current.clearValue();
  };

  const handleChapterClear = () => {
    if (selectChapterRef.current) selectChapterRef.current.clearValue();
    if (selectMemberRef.current) selectMemberRef.current.clearValue();
  };

  const handleMemberClear = () => {
    if (selectMemberRef.current) selectMemberRef.current.clearValue();
  };

  const auth_user = JSON.parse(localStorage.getItem("user"));
  const isSuperAdmin = auth_user?.user_type === 1 ? 1 : 0;
  const isRegionAdmin = auth_user?.user_type === 2 ? 1 : 0;
  const isChapterAdmin = auth_user?.user_type === 3 ? 1 : 0;

  const statusOptions = [
    { value: 0, label: "Pending" },
    { value: 1, label: "Paid" },
  ];

  const handleDateRangeClear = () => {
    setShowClearBtn(false);
    applyFilter({
      ...filter,
      month_date: "",
    });
  };

  const fetchFeesData = async (id) => {
    try {
      axios.get(WEEKLY_FEES_EDIT + id).then((res) => {
        if (res.status === 200 && res.data.success) {
          let responseData = res.data.data;
          setFeesModal(true);
          setFeesData({
            id: responseData?.id,
            fee_amount: responseData?.fee_amount,
          });
        }
      });
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const updateFees = async (id) => {
    try {
      const response = await axios.put(WEEKLY_FEES_UPDATE + id, feesData);
      if (response.status === 200 && response.data.success) {
        toast.success(response.data.msg);
        setFeesModal(false);
        // Update local state without whole page refresh
        setData((prevData) =>
          prevData.map((item) =>
            item.id === id
              ? { ...item, fee_amount: feesData.fee_amount }
              : item,
          ),
        );
      }
    } catch (err) {
      if (err.response.status === 422) {
        const error = err.response.data.error;
        setErrors(error);
        toast.error(err.response.data.msg);
      }
    }
  };

  useEffect(() => {
    setLoading(true);
    axios
      .get(MEETING_WEEKLY_FEES_LIST, {
        params: {
          type: PAGINATE,
          page: page,
          perPage: perPage,
          isAdmin: 1,
          ...filter,
        },
      })
      .then((response) => {
        if (response.status === 200 && response.data.success) {
          setData(response.data.data.schedules);
          setPaginator({
            prev: response.data.data.prev_page_url != null,
            next: response.data.data.next_page_url != null,
            total: response.data.data.total,
            current: response.data.data.current_page,
            last: response.data.data.last_page,
          });
        }
      })
      .finally(() => {
        setLoading(false); // Set loading to false after data is fetched
      });

    const fetchData = async () => {
      try {
        // Fetch user data
        const region_id = isRegionAdmin === 1 ? auth_user?.region_id : "";
        const chapter_id = isChapterAdmin === 1 ? auth_user?.chapter_id : "";

        const userResponse = await axios.get(USER_LIST_DROPDOWN, {
          params: {
            ...filter,
            exclude_other_region_chapter_users: 1,
          },
        });
        const fetchedUserOptions = userResponse.data.data;
        setUserOptions(fetchedUserOptions);

        // Fetch country data
        const countryResponse = await axios.get(COUNTRY_LIST_DROPDOWN);
        const fetchedCountryOptions = countryResponse.data.data;
        setCountryOptions(fetchedCountryOptions);

        // Fetch state data
        const stateResponse = await axios.get(STATE_LIST_DROPDOWN, {
          params: {
            ...filter,
          },
        });
        const fetchedStateOptions = stateResponse.data.data;
        setStateOptions(fetchedStateOptions);

        // Fetch region data
        const regionResponse = await axios.get(REGION_LIST_DROPDOWN, {
          params: {
            ...filter,
          },
        });
        const fetchedRegionOptions = regionResponse.data.data;
        setRegionOptions(fetchedRegionOptions);

        // Fetch chapter data
        const chapterResponse = await axios.get(CHAPTER_LIST_DROPDOWN, {
          params: {
            ...filter,
          },
        });
        const fetchedChapterOptions = chapterResponse.data.data;
        setChapterOptions(fetchedChapterOptions);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, [page, refresh, perPage, filter]);

  const handleCalendarClick = () => {
    if (datePickerRef.current) {
      datePickerRef.current.setOpen(true);
    }
  };

  return (
    <div className="content-wrapper">
      <div className="container-fluid p-0 mt-0 mb-2">
        <div className="col-lg-12">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <h5 className="page-title">Meeting Fees List</h5>
              <ol className="breadcrumb">
                <li>
                  <div className="breadcrumb_f">
                    <div>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="50"
                        height="50"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#212b39"
                        strokeWidth="1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                        <polyline points="9 22 9 12 15 12 15 22"></polyline>
                      </svg>
                    </div>
                    <div>
                      <Link to={"/dashboard"}>Dashboard</Link>
                    </div>
                  </div>
                </li>
                <li>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="50"
                    height="50"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#212b39"
                    strokeWidth="1"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </li>
                <li>
                  <span className="active_breadcrumb">
                    Meeting Wise Fees List
                  </span>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>
      <div className="flex-grow-1">
        <div className="card icard sticky-table-card">
          {/* <div className="card-header">
            <h5>Meeting Fees List</h5>
          </div> */}
          <div className="container-fluid f_containter">
            <div className="row mb-2">
              {hasRole([1]) && (
                <div className="mb-3 col-md-3">
                  <label className="form-label" htmlFor="country_id">
                    Country
                  </label>
                  <Select
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                    name="country_id"
                    isClearable
                    options={countryOptions}
                    onChange={(selected) => {
                      handleStateClear();
                      applyFilter({
                        ...filter,
                        country_id: selected?.value,
                        state_id: null,
                        region_id: null,
                        chapter_id: null,
                        user_id: null,
                      });
                    }}
                  />
                </div>
              )}

              {hasRole([1, 6]) && (
                <div className="mb-3 col-md-3">
                  <label className="form-label" htmlFor="state_id">
                    State
                  </label>
                  <Select
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                    ref={selectStateRef}
                    name="state_id"
                    isClearable
                    options={stateOptions}
                    onChange={(selected) => {
                      handleRegionClear();
                      applyFilter({
                        ...filter,
                        state_id: selected?.value,
                        region_id: null,
                        chapter_id: null,
                        user_id: null,
                      });
                    }}
                  />
                </div>
              )}

              {hasRole([1, 6, 7]) && (
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label" htmlFor="region_id">
                      Region
                    </label>
                    <Select
                      ref={selectRegionRef}
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                      name="region_id"
                      isClearable
                      options={regionOptions}
                      onChange={(selected) => {
                        handleChapterClear();
                        applyFilter({
                          ...filter,
                          region_id: selected?.value,
                          chapter_id: null,
                          user_id: null,
                        });
                      }}
                    />
                  </div>
                </div>
              )}

              {hasRole([1, 6, 7, 2]) && (
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label" htmlFor="chapter_id">
                      Chapter
                    </label>
                    <Select
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                      ref={selectChapterRef}
                      name="chapter_id"
                      isClearable
                      options={chapterOptions}
                      onChange={(selected) => {
                        handleMemberClear();
                        applyFilter({
                          ...filter,
                          chapter_id: selected?.value,
                          user_id: null,
                        });
                      }}
                    />
                  </div>
                </div>
              )}

              <div className="col-md-3">
                <div className="mb-3">
                  <label className="form-label" htmlFor="user_id">
                    Member
                  </label>
                  <Select
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                    ref={selectMemberRef}
                    name="user_id"
                    isClearable
                    options={userOptions}
                    onChange={(selected) =>
                      applyFilter({ ...filter, user_id: selected?.value })
                    }
                  />
                </div>
              </div>
              <div className="col-md-3">
                <div className="mb-3">
                  <label className="form-label" htmlFor="payment_status">
                    Payment Status
                  </label>
                  <Select
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                    name="payment_status"
                    isClearable
                    options={statusOptions}
                    onChange={(selected) =>
                      applyFilter({ ...filter, payment_status: selected?.value })
                    }
                  />
                </div>
              </div>
              <div className="col-md-3">
                <div className="mb-3 dateRangeDiv">
                  <label className="form-label" htmlFor="month_date">
                    Month
                  </label>
                  <div className="datepicker_wrapper">
                    <div className="datepicker">
                      <DatePicker
                        ref={datePickerRef}
                        portalId="root-portal"
                        wrapperClassName="w-100 d-block"
                        className="form-control"
                        selected={
                          filter.month_date
                            ? moment(filter.month_date, "YYYY-MM").toDate()
                            : null
                        }
                        onChange={(date) => {
                          if (date) {
                            setShowClearBtn(true);
                            applyFilter({
                              ...filter,
                              month_date: moment(date).format("YYYY-MM"),
                            });
                          } else {
                            setShowClearBtn(false);
                            applyFilter({
                              ...filter,
                              month_date: "",
                            });
                          }
                        }}
                        dateFormat="MMMM yyyy"
                        showMonthYearPicker
                        placeholderText="Select Month"
                      />
                      {showClearBtn && (
                        <button
                          className="border-0 clearDateRangeBtn"
                          data-bs-toggle="tooltip"
                          data-bs-offset="0,4"
                          data-bs-placement="top"
                          data-bs-html="true"
                          title="Clear"
                          data-bs-original-title="Clear"
                          onClick={handleDateRangeClear}
                        >
                          <i className="bx bx-x"></i>
                        </button>
                      )}
                    </div>
                    <div className="cal_icon" onClick={handleCalendarClick}>
                      <span className="input-group-text calendar-icon"></span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Pagination Component */}
          {data.length > 0 && (
            <Pagination
              paginator={paginator}
              page={(no) => setPage(no)}
              perPage={perPage}
              setPerPage={setPerPage}
            />
          )}
          <StickyTable refreshDeps={[data, loading]}><table className="table table-striped itable sticky-action-col">
              <thead>
                <tr>
                  <th>S.NO</th>
                  {hasRole([1]) && <th>Country</th>}
                  {hasRole([1, 6]) && <th>State</th>}
                  {hasRole([1, 6, 7]) && <th>Region</th>}
                  {hasRole([1, 6, 7, 2]) && <th>Chapter</th>}
                  <th>Member</th>
                  <th>Meeting Fees</th>
                  <th>Meeting Date</th>
                  <th>Meeting Status</th>
                  <th style={{ textAlign: "center", width: "10%" }}>Action</th>
                </tr>
              </thead>
              <tbody className="table-border-bottom-0">
                {loading ? (
                  <Loader loading={loading} colspan="10" />
                ) : data.length > 0 ? (
                  data.map((schedule, i) => (
                    <tr key={i}>
                      <td>{schedule?.sno}</td>
                      {hasRole([1]) && <td>{schedule?.country_name}</td>}
                      {hasRole([1, 6]) && <td>{schedule?.state_name || "Tamil Nadu"}</td>}
                      {hasRole([1, 6, 7]) && <td>{schedule?.region_name}</td>}
                      {hasRole([1, 6, 7, 2]) && (
                        <td>{schedule?.chapter_name}</td>
                      )}
                      <td>
                        <span className="table_img pr-2">
                          <img src={schedule?.user_profile_image} alt="icon" />
                        </span>
                        {schedule?.user_name}
                      </td>
                      <td>{schedule?.fee_amount}</td>
                      <td>{schedule?.meeting_date}</td>
                      <td>{schedule?.meeting_status_text || "Scheduled"}</td>
                      <td style={{ textAlign: "right" }}>
                        {hasPermission("monthly-fees-edit") && Number(schedule?.meeting_status) !== 2 && (
                          <button
                            className="badge bg-label-success me-1 border-0"
                            data-bs-toggle="tooltip"
                            data-bs-offset="0,4"
                            data-bs-placement="top"
                            data-bs-html="true"
                            title="Edit fees amount"
                            data-bs-original-title="Edit fees amount"
                            onClick={() => {
                              fetchFeesData(schedule?.id);
                            }}
                          >
                            <i className="bx bx-pencil"></i>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <NoDataFound colspan="10" message="No Data Found" />
                )}
              </tbody>
            </table>
          </StickyTable>
          {/* Pagination Component */}
          {data.length > 0 && (
            <Pagination
              paginator={paginator}
              page={(no) => setPage(no)}
              perPage={perPage}
              setPerPage={setPerPage}
            />
          )}
        </div>
      </div>

      <Modal
        show={feesModal}
        size="l"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        onHide={() => setFeesModal(false)}
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title
            id="contained-modal-title-vcenter"
            className="modal_title1"
          >
            Fees Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="card-body py-0">
            <div className="row">
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="form-label" htmlFor="fee_amount">
                    Fee Amount<span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    className="form-control"
                    id="fee_amount"
                    value={feesData?.fee_amount || ""}
                    onChange={(e) =>
                      setFeesData({ ...feesData, fee_amount: e.target.value })
                    }
                    placeholder="Enter fee amount"
                  />
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-success isubmit_button"
            data-bs-dismiss="modal"
            onClick={() => updateFees(feesData?.id)}
          >
            Update
          </button>
          <button
            type="button"
            className="btn btn-secondary ibutton"
            data-bs-dismiss="modal"
            onClick={() => setFeesModal(false)}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default WeeklyFeesList;
